import { useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  type AthleteTargets,
  type TargetItem,
  type UnitsPreference,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { DisclosureCard } from "../primitives/DisclosureCard";
import { EditorTopBar } from "../primitives/EditorTopBar";
import { UnsavedChangesDialog } from "../primitives/UnsavedChangesDialog";
import { useBreakpoint } from "../useBreakpoint";
import type { ProgressDeps } from "../Progress/types";
import { useExerciseNameResolver } from "../Progress/useExerciseNameResolver";
import { useSessionResultsFromDeps } from "../Progress/useSessionResults";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { makeEditorStyles } from "../ProgramEditor/styles";
import { makeComposerStyles } from "../ProgramComposer/composerStyles";
import { makePlanStyles } from "../ProgramComposer/planComposerStyles";
import { WeeklyTargetsSection } from "../ProgramComposer/WeeklyTargetsSection";
import { TargetHistoryList } from "./TargetHistoryList";
import { useTargetsDoc, type TargetsSaveFailure } from "./useTargetsDoc";

// The weekly-target editor — the athlete's OWN targets (ME-3). ONE home now:
// the AthleteTargets doc, edited by you on Me or by a coach for an athlete
// (athleteSub). The old program-targets home retired with ME-3.
//
// A NAVIGATED surface (not a modal): the host swaps it in, OUTSIDE its
// ScrollView, so the <EditorTopBar> stays sticky above the editor's own scroll.
// It composes primitives — <EditorTopBar> (‹ back · Save) + <WeeklyTargetsSection
// defaultOpen> (the composer's targets band) + <TargetHistoryList> — over
// useTargetsDoc (the ETag-locked read/write). It is NOT the ComposerEditLayout
// shell: no spine, no master-detail, no exercise picker — just the targets.
//
// No program is in scope, so no meta and no prescription reference; the exercise
// pool is what the athlete has actually trained (a target on an exercise you've
// never done has nothing to score against).
//
// Reference numbers ("you average 10.2/week") come from the SAME session-results
// cache the host already warmed (getCached is synchronous), so they paint with
// the editor, not after a spinner — the reason the host owns `deps`.
//
// Save is gated until useTargetsDoc has read the ETag: without it a PATCH would
// 428. The lock is fast, so this is a brief, honest greyed-out Save, not a
// spinner.

/** Whose targets are being edited. */
export type TargetsEditorSource = {
  /** Absent = your own targets; a sub = a coach editing that athlete's targets
   *  (the same AthleteTargets doc, via ?athleteSub). */
  athleteSub?: string;
  onSaved?: (targets: AthleteTargets) => void;
};

export type TargetsEditorProps = {
  theme: UiTheme;
  /** Base apiFetch (NOT the athlete-scoped wrapper) — carries If-Match. */
  apiFetch: ProgramApiFetch;
  /** The reference session-results cache lives here (athlete-scoped for a coach). */
  deps: ProgressDeps;
  source: TargetsEditorSource;
  /** Author label for a history entry's `bySub` — the host knows who "you" are. */
  resolveAuthor: (bySub: string) => string;
  units: UnitsPreference;
  weekStartDay?: number;
  locale: string;
  /** Left nav affordance ("‹ Leonardo") + its action. */
  backLabel: string;
  onClose: () => void;
  /** TG-1 — hands the host the SAME dirty-gated back the header's ‹ uses, so
   *  a platform back (Android's hardware button, a web route change) can run
   *  the confirm instead of discarding behind the editor's back. Returns true
   *  when it consumed the event (the confirm is now on screen), false when
   *  there was nothing to protect and the host should proceed with its own
   *  close. The BackHandler itself stays in the mount — it is a platform seam,
   *  and packages/ui deliberately does not import one. */
  registerBackGuard?: (guard: () => boolean) => void;
};

// This copy is written for the coach reading it about an ATHLETE's program
// (no_grant / not_coach / multi_assignee are coach-only-reachable failure
// modes — see useTargetsDoc). `not_found` is the one reason both paths can
// hit, and its coach phrasing names a "program": the self path edits a
// personal targets doc, never a program, so that word is wrong there (ME-3a).
const SAVE_ERROR_KEY: Record<TargetsSaveFailure, string> = {
  stale: "coach.targets.errStale",
  no_grant: "coach.targets.errNoGrant",
  not_coach: "coach.targets.errNotCoach",
  multi_assignee: "coach.targets.errMultiAssignee",
  missing_validator: "coach.targets.errRetry",
  not_found: "coach.targets.errNotFound",
  offline_or_unknown: "coach.targets.errOffline",
};

const SAVE_ERROR_DEFAULT: Record<TargetsSaveFailure, string> = {
  stale: "These targets changed while you were editing. Reload and re-apply.",
  no_grant: "The athlete hasn't granted you target edits.",
  not_coach: "You aren't this athlete's coach.",
  multi_assignee: "This program is shared by more than one athlete.",
  missing_validator: "Still loading — try again in a moment.",
  not_found: "This program no longer exists.",
  offline_or_unknown: "Couldn't save. Check your connection and try again.",
};

// Self-path sibling for `not_found` only (ME-3a) — the other coach-labelled
// reasons above are unreachable when `source.athleteSub` is unset, so they
// have no self copy to leak.
const SAVE_ERROR_KEY_SELF_NOT_FOUND = "me.targets.errNotFound";
const SAVE_ERROR_DEFAULT_SELF_NOT_FOUND =
  "Couldn't find your targets. Go back and retry.";

const docItems = (doc: AthleteTargets): TargetItem[] => doc.items;

export function TargetsEditor({
  theme,
  apiFetch,
  deps,
  source,
  resolveAuthor,
  units,
  weekStartDay,
  locale,
  backLabel,
  onClose,
  registerBackGuard,
}: TargetsEditorProps) {
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const p = theme.palette;
  const cs = useMemo(() => makeComposerStyles(p), [p]);
  const ps = useMemo(() => makePlanStyles(p), [p]);
  const selectStyles = useMemo(() => makeEditorStyles(p), [p]);
  const styles = useMemo(() => makeStyles(theme), [theme]);

  // Same single-flight cache the host warmed → getCached() is synchronous.
  const { results, catalog } = useSessionResultsFromDeps(deps);

  // `source` is rebuilt by the host on every render; narrow athleteSub once so
  // the memos below key on the stable string (a coach editing this athlete).
  const athleteSub = source.athleteSub;

  const paths = useMemo(() => {
    const q = athleteSub ? `?athleteSub=${encodeURIComponent(athleteSub)}` : "";
    return {
      doc: `/api/me/targets${q}`,
      history: `/api/me/targets/history${q}`,
      patch: `/api/me/targets${q}`,
    };
  }, [athleteSub]);

  const { doc, targets, history, historyTruncated, loadFailed, saving, save } =
    useTargetsDoc<AthleteTargets>({ apiFetch, paths, itemsOf: docItems });

  // Nothing on screen to seed from, so seed from the read — ONCE: a background
  // reload after a save must not clobber what is being edited.
  const [items, setItems] = useState<TargetItem[]>([]);
  const seededRef = useRef(false);
  // The dirty baseline (TG-1) — a SNAPSHOT taken at the same moment as the
  // seed, not a live read of `targets`: `targets` is recomputed off `doc`,
  // which useTargetsDoc silently re-reads after a save/stale-reload, and
  // comparing against a moving target would misjudge dirty either way. Frozen
  // once here, exactly like `seededRef` freezes the seed itself.
  const baselineRef = useRef<TargetItem[] | null>(null);
  useEffect(() => {
    if (seededRef.current || doc === null) return;
    seededRef.current = true;
    setItems(targets);
    baselineRef.current = targets;
  }, [doc, targets]);

  // Dirty = current edits differ from the doc as it was when the editor
  // opened. `baselineRef` is null until the seed effect above has run, so a
  // still-loading editor (nothing to compare against yet) is never dirty —
  // the false-positive this whole check exists to avoid (a confirm that
  // fires on a doc that was never touched trains the user to ignore it).
  const isDirty =
    baselineRef.current !== null &&
    JSON.stringify(items) !== JSON.stringify(baselineRef.current);

  // TG-1 — back (EditorTopBar's ‹) with unsaved edits confirms before
  // discarding; a clean editor (or one you've already saved) leaves straight
  // through. Save itself never routes through here: onClose after a
  // successful save is closing over data that WAS saved, not discarding it.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const handleBack = () => {
    if (isDirty) {
      setConfirmingDiscard(true);
      return;
    }
    onClose();
  };

  // The guard the host calls from its own back handling. Read through a ref so
  // the function handed out once at mount always sees the CURRENT dirty state
  // — a stale closure here would be the whole bug back again, silently.
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;
  useEffect(() => {
    if (!registerBackGuard) return;
    registerBackGuard(() => {
      if (!dirtyRef.current) return false;
      setConfirmingDiscard(true);
      return true;
    });
  }, [registerBackGuard]);

  const [err, setErr] = useState<string | null>(null);

  // Resolve BY SLUG against the live catalog, with the write-time name as the
  // fallback when the catalog row is gone — the shared Progress resolver.
  const resolveExerciseName = useExerciseNameResolver(catalog);

  // The exercises you can put a target on: the ones the athlete has actually
  // trained — a target on an exercise you've never done has nothing to score
  // against, and the whole catalog would be a scroll, not a choice. Reads
  // alphabetically (unlike a program's meaningful slot order).
  const exercisePool = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of results ?? []) {
      for (const er of r.result.exerciseResults) {
        if (!seen.has(er.exerciseSlug)) {
          seen.set(
            er.exerciseSlug,
            resolveExerciseName(er.exerciseSlug, er.exerciseName),
          );
        }
      }
    }
    return [...seen]
      .map(([slug, name]) => ({ slug, name }))
      .sort((a, b) => a.name.localeCompare(b.name, locale));
  }, [results, resolveExerciseName, locale]);

  // A sub in `source` means a coach is editing an ATHLETE's targets; its
  // absence means you're editing your own — the self path (ME-3a).
  const isSelf = !athleteSub;

  const handleSave = async () => {
    setErr(null);
    const res = await save(items);
    if (!res.ok) {
      const useSelfCopy = isSelf && res.reason === "not_found";
      const key = useSelfCopy
        ? SAVE_ERROR_KEY_SELF_NOT_FOUND
        : SAVE_ERROR_KEY[res.reason];
      const defaultValue = useSelfCopy
        ? SAVE_ERROR_DEFAULT_SELF_NOT_FOUND
        : SAVE_ERROR_DEFAULT[res.reason];
      setErr(t(key, { defaultValue }));
      return;
    }
    source.onSaved?.(res.doc);
    onClose();
  };

  const historyTitle = t("coach.targets.historyTitle", {
    defaultValue: "Change history",
  });

  const targetsSection = (
    <WeeklyTargetsSection
      theme={theme}
      cs={cs}
      ps={ps}
      selectStyles={selectStyles}
      programExercises={exercisePool}
      sessionResults={results ?? undefined}
      exerciseCatalog={catalog ?? undefined}
      unitsPreference={units}
      weekStartDay={weekStartDay}
      items={items}
      onChange={setItems}
      defaultOpen
    />
  );

  const historyList = (
    <TargetHistoryList
      theme={theme}
      history={history}
      historyTruncated={historyTruncated}
      loadFailed={loadFailed}
      resolveAuthor={resolveAuthor}
      resolveExerciseName={resolveExerciseName}
      units={units}
      locale={locale}
    />
  );

  return (
    <View style={styles.wrap}>
      <EditorTopBar
        theme={theme}
        backLabel={backLabel}
        onBack={handleBack}
        title={t("me.targets.title", { defaultValue: "Goals" })}
        onSave={handleSave}
        busy={saving}
        saveDisabled={doc === null}
        error={
          err ??
          (loadFailed
            ? t("coach.targets.errLoad", {
                defaultValue: "Couldn't load these targets. Go back and retry.",
              })
            : null)
        }
      />

      <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
      >
        {wide ? (
          // Desktop console: the targets form takes the slack, the audit trail
          // sits in a fixed right rail (a View, never a nested ScrollView).
          <View style={styles.wideRow}>
            <View style={styles.wideMain}>{targetsSection}</View>
            <View style={styles.wideRail}>
              <Text style={styles.historyTitle}>{historyTitle}</Text>
              {historyList}
            </View>
          </View>
        ) : (
          // Phone: the history folds away below the form (targets are the point;
          // the trail is on demand).
          <>
            {targetsSection}
            <DisclosureCard
              theme={theme}
              accessibilityLabel={historyTitle}
              header={<Text style={styles.historyTitle}>{historyTitle}</Text>}
            >
              {historyList}
            </DisclosureCard>
          </>
        )}
      </ScrollView>

      <UnsavedChangesDialog
        theme={theme}
        visible={confirmingDiscard}
        onKeepEditing={() => setConfirmingDiscard(false)}
        onDiscard={() => {
          setConfirmingDiscard(false);
          onClose();
        }}
      />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { flex: 1, backgroundColor: p.bg },
    content: { padding: s.s4, gap: s.s4, paddingBottom: 48 },
    // Desktop console: form left (takes the slack), audit trail in a fixed rail.
    wideRow: { flexDirection: "row", gap: s.s4, alignItems: "flex-start" },
    wideMain: { flex: 1, gap: s.s4 },
    wideRail: { width: 320, gap: s.s2 },
    historyTitle: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: "700",
      paddingBottom: s.s1,
    },
  });
}
